import {isBrowser} from './utils';

type NavigationListener = () => void;

const listeners = new Set<NavigationListener>();
let originalPushState: History['pushState'] | null = null;
let originalReplaceState: History['replaceState'] | null = null;
let patchedPushState: History['pushState'] | null = null;
let patchedReplaceState: History['replaceState'] | null = null;

function notify(): void {
  Promise.resolve().then(() => {
    listeners.forEach(listener => listener());
  });
}

function install(): void {
  if (!isBrowser() || originalPushState) return;

  originalPushState = window.history.pushState;
  originalReplaceState = window.history.replaceState;

  patchedPushState = function pushState(
    this: History,
    data: unknown,
    unused: string,
    url?: string | URL | null,
  ): void {
    originalPushState?.call(this, data, unused, url);
    notify();
  };
  patchedReplaceState = function replaceState(
    this: History,
    data: unknown,
    unused: string,
    url?: string | URL | null,
  ): void {
    originalReplaceState?.call(this, data, unused, url);
    notify();
  };

  window.history.pushState = patchedPushState;
  window.history.replaceState = patchedReplaceState;
  window.addEventListener('popstate', notify);
  window.addEventListener('hashchange', notify);
}

function uninstall(): void {
  if (!isBrowser() || listeners.size || !originalPushState || !originalReplaceState) return;

  if (window.history.pushState === patchedPushState) {
    window.history.pushState = originalPushState;
  }
  if (window.history.replaceState === patchedReplaceState) {
    window.history.replaceState = originalReplaceState;
  }
  window.removeEventListener('popstate', notify);
  window.removeEventListener('hashchange', notify);

  originalPushState = null;
  originalReplaceState = null;
  patchedPushState = null;
  patchedReplaceState = null;
}

export function subscribeToNavigation(listener: NavigationListener): () => void {
  if (!isBrowser()) return () => undefined;
  listeners.add(listener);
  install();

  let subscribed = true;
  return () => {
    if (!subscribed) return;
    subscribed = false;
    listeners.delete(listener);
    uninstall();
  };
}
